import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Calendar, MapPin, Award, AlertCircle } from 'lucide-react';
import { reportesJugadoresService } from '../../services/reportesJugadoresService';
import type {
  CreateReporteJugadorRequest,
  PlayerSearchResult,
  CompetitionSearchResult,
  TeamSearchResult
} from '../../types/reportesJugadores';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { translatePosition } from '../../utils/positionTranslations';

const ReporteJugadorFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Player autocomplete state
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerResults, setPlayerResults] = useState<PlayerSearchResult[]>([]);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSearchResult | null>(null);
  const playerSearchRef = useRef<HTMLDivElement>(null);

  // Competition autocomplete state
  const [competitionSearch, setCompetitionSearch] = useState('');
  const [competitionResults, setCompetitionResults] = useState<CompetitionSearchResult[]>([]);
  const [showCompetitionDropdown, setShowCompetitionDropdown] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<CompetitionSearchResult | null>(null);
  const competitionSearchRef = useRef<HTMLDivElement>(null);

  // Team autocomplete state
  const [teamSearch, setTeamSearch] = useState('');
  const [teamResults, setTeamResults] = useState<TeamSearchResult[]>([]);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [, setSelectedTeam] = useState<TeamSearchResult | null>(null);

  const [formData, setFormData] = useState<CreateReporteJugadorRequest>({
    id_jugador: '',
    fecha_informe: new Date().toISOString().split('T')[0],
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (playerSearchRef.current && !playerSearchRef.current.contains(event.target as Node)) {
        setShowPlayerDropdown(false);
      }
      if (competitionSearchRef.current && !competitionSearchRef.current.contains(event.target as Node)) {
        setShowCompetitionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Player search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (playerSearch.length >= 2) {
        searchPlayers(playerSearch);
      } else {
        setPlayerResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [playerSearch]);

  // Competition search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (competitionSearch.length >= 2) {
        searchCompetitions(competitionSearch);
      } else {
        setCompetitionResults([]);
        setShowCompetitionDropdown(false);
      }
    }, 300);
  
    return () => clearTimeout(delayDebounceFn);
  }, [competitionSearch]);
  

  // Team search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (teamSearch.length >= 2) {
        searchTeams(teamSearch);
      } else {
        setTeamResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [teamSearch]);

  const searchPlayers = async (query: string) => {
    const response = await reportesJugadoresService.searchPlayers(query);
    if (response.success && response.data) {
      setPlayerResults(response.data);
      setShowPlayerDropdown(true);
    }
  };

  const searchCompetitions = async (query: string) => {
    const response = await reportesJugadoresService.searchCompetitions(query);
    if (response.success && response.data) {
      setCompetitionResults(response.data);
      setShowCompetitionDropdown(true);
    }
  };

  const handleCompetitionFocus = async () => {
    // Load all competitions when focusing the field
    if (competitionResults.length >= 2) {
      setShowCompetitionDropdown(true);
    } else {
      setShowCompetitionDropdown(false);
    }
  };

  const searchTeams = async (query: string) => {
    const response = await reportesJugadoresService.searchTeams(query);
    if (response.success && response.data) {
      setTeamResults(response.data);
      setShowTeamDropdown(true);
    }
  };

  const handlePlayerSelect = (player: PlayerSearchResult) => {
    setSelectedPlayer(player);
    setPlayerSearch(player.player_name);
    setFormData(prev => ({ ...prev, id_jugador: player.player_id }));
    setShowPlayerDropdown(false);
  };

  const handleCompetitionSelect = (competition: CompetitionSearchResult) => {
    setSelectedCompetition(competition);
    setCompetitionSearch(competition.competition_name);
    setFormData(prev => ({
      ...prev,
      id_competicion: competition.competition_id,
      nombre_competicion: competition.competition_name
    }));
    setShowCompetitionDropdown(false);
  };

  const handleTeamSelect = (team: TeamSearchResult) => {
    setSelectedTeam(team);
    setTeamSearch(team.club_name);
    setFormData(prev => ({
      ...prev,
      id_equipo_rival: team.club_id,
      nombre_equipo_rival: team.club_name
    }));
    setShowTeamDropdown(false);
  };

  const handleInputChange = (field: keyof CreateReporteJugadorRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.id_jugador) {
      setError('Debes seleccionar un jugador');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await reportesJugadoresService.createReporte(formData);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/sporting/reportes-jugadores');
        }, 2000);
      } else {
        setError(response.error || 'Error al crear el reporte');
      }
    } catch (err) {
      setError('Error de conexión al crear el reporte');
    } finally {
      setLoading(false);
    }
  };

  const renderRatingInput = (field: keyof CreateReporteJugadorRequest, label: string) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <input
        type="number"
        min="0"
        max="10"
        step="0.5"
        value={formData[field] as number || ''}
        onChange={(e) => handleInputChange(field, e.target.value ? parseFloat(e.target.value) : undefined)}
        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="0.0 - 10.0"
      />
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Reporte Creado!</h2>
          <p className="text-gray-600">Redirigiendo a la lista de reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/sporting/reportes-jugadores')}
            className="p-2 text-gray-400 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Nuevo <span className="text-blue-600">Reporte de Jugador</span>
            </h1>
            <p className="text-gray-600">
              Crea un reporte detallado de scouting
            </p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Información Básica */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <User className="w-5 h-5 text-blue-600" />
            Información Básica
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Player Search with Autocomplete */}
            <div className="md:col-span-2" ref={playerSearchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jugador *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={playerSearch}
                  onChange={(e) => {
                    setPlayerSearch(e.target.value);
                    setSelectedPlayer(null);
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Buscar jugador por nombre..."
                  required
                />
                
                {/* Dropdown de resultados */}
                {showPlayerDropdown && playerResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {playerResults.map((player) => (
                      <div
                        key={player.player_id}
                        onClick={() => handlePlayerSelect(player)}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0"
                      >
                        <div className="text-gray-900 font-medium">{player.player_name}</div>
                        <div className="text-sm text-gray-600">
                          {translatePosition(player.position || '', currentLang)} {player.current_club_name && `• ${player.current_club_name}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedPlayer && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ Jugador seleccionado: {selectedPlayer.player_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha del Informe
              </label>
              <input
                type="date"
                value={formData.fecha_informe}
                onChange={(e) => handleInputChange('fecha_informe', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha del Partido
              </label>
              <input
                type="date"
                value={formData.fecha_partido || ''}
                onChange={(e) => handleInputChange('fecha_partido', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Posición Principal
              </label>
              <select
                value={formData.posicion_principal || ''}
                onChange={(e) => handleInputChange('posicion_principal', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar...</option>
                {['Portero', 'Defensa Central', 'Lateral Derecho', 'Lateral Izquierdo', 'Pivote', 'Mediocentro', 'Mediocentro Ofensivo', 'Extremo Derecho', 'Extremo Izquierdo', 'Mediapunta', 'Delantero Centro', 'Segundo Delantero'].map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Posición Secundaria
              </label>
              <select
                value={formData.posicion_secundaria || ''}
                onChange={(e) => handleInputChange('posicion_secundaria', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar...</option>
                {['Portero', 'Defensa Central', 'Lateral Derecho', 'Lateral Izquierdo', 'Pivote', 'Mediocentro', 'Mediocentro Ofensivo', 'Extremo Derecho', 'Extremo Izquierdo', 'Mediapunta', 'Delantero Centro', 'Segundo Delantero'].map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pie Preferido
              </label>
              <select
                value={formData.pie_preferido || ''}
                onChange={(e) => handleInputChange('pie_preferido', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar...</option>
                {['Derecho', 'Izquierdo', 'Ambidiestro'].map(pie => (
                  <option key={pie} value={pie}>{pie}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Información del Partido */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Calendar className="w-5 h-5 text-blue-600" />
            Información del Partido
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Competition Search */}
            <div className="md:col-span-2" ref={competitionSearchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Competición
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={competitionSearch}
                  onChange={(e) => {
                    setCompetitionSearch(e.target.value);
                    setSelectedCompetition(null);
                  }}
                  onFocus={handleCompetitionFocus}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Buscar o seleccionar competición..."
                />
                
                {showCompetitionDropdown && competitionResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {competitionResults.map((comp, index) => (
                      <div
                        key={`${comp.competition_id}-${index}`}
                        onClick={() => handleCompetitionSelect(comp)}
                        className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0"
                      >
                        <div className="text-gray-900">{comp.competition_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedCompetition && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ Competición seleccionada: {selectedCompetition.competition_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jornada
              </label>
              <input
                type="number"
                value={formData.jornada || ''}
                onChange={(e) => handleInputChange('jornada', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: 15"
              />
            </div>

            {/* Team Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipo Rival
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={teamSearch}
                  onChange={(e) => {
                    setTeamSearch(e.target.value);
                    setSelectedTeam(null);
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Buscar equipo..."
                />
                
                {showTeamDropdown && teamResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {teamResults.map((team) => (
                      <div
                        key={team.club_id}
                        onClick={() => handleTeamSelect(team)}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0"
                      >
                        <div className="text-gray-900 font-medium">{team.club_name}</div>
                        {team.country_name && (
                          <div className="text-sm text-gray-600">{team.country_name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lugar del Partido
              </label>
              <select
                value={formData.lugar_partido || ''}
                onChange={(e) => handleInputChange('lugar_partido', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar...</option>
                {['Local', 'Visitante', 'Neutral'].map(lugar => (
                  <option key={lugar} value={lugar}>{lugar}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minutos Jugados
              </label>
              <input
                type="number"
                min="0"
                max="120"
                value={formData.minutos_jugados || ''}
                onChange={(e) => handleInputChange('minutos_jugados', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: 90"
              />
            </div>
          </div>
        </div>

        {/* Aspectos Físicos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-blue-600" />
            Aspectos Físicos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_velocidad', 'Velocidad')}
            {renderRatingInput('nota_complexion', 'Complexión')}
            {renderRatingInput('nota_potencia', 'Potencia')}
          </div>
        </div>

        {/* Aspectos Defensivos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-red-600" />
            Aspectos Defensivos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_duelos_defensivos', 'Duelos Defensivos')}
            {renderRatingInput('nota_tactica_defensiva', 'Táctica Defensiva')}
            {renderRatingInput('nota_lectura_defensiva', 'Lectura Defensiva')}
            {renderRatingInput('nota_1x1_defensivo', '1x1 Defensivo')}
            {renderRatingInput('nota_juego_aereo_defensivo', 'Juego Aéreo Defensivo')}
          </div>
        </div>

        {/* Aspectos Ofensivos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-green-600" />
            Aspectos Ofensivos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_juego_aereo_ofensivo', 'Juego Aéreo Ofensivo')}
            {renderRatingInput('nota_marcaje_area', 'Marcaje en Área')}
            {renderRatingInput('nota_capacidad_ofensiva', 'Capacidad Ofensiva')}
            {renderRatingInput('nota_calidad_ofensiva', 'Calidad Ofensiva')}
          </div>
        </div>

        {/* Calidad Técnica */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-yellow-600" />
            Calidad Técnica
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_calidad_juego_corto', 'Juego Corto')}
            {renderRatingInput('nota_calidad_juego_largo', 'Juego Largo')}
            {renderRatingInput('nota_saque_banda', 'Saque de Banda')}
          </div>
        </div>

        {/* Capacidades Generales */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-purple-600" />
            Capacidades Generales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_capacidad_juego', 'Capacidad de Juego')}
            {renderRatingInput('nota_capacidad_sacrificio', 'Capacidad de Sacrificio')}
          </div>
        </div>

        {/* Aspectos Mentales */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-indigo-600" />
            Aspectos Mentales y Tácticos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderRatingInput('nota_comunicacion', 'Comunicación')}
            {renderRatingInput('nota_toma_decisiones', 'Toma de Decisiones')}
            {renderRatingInput('nota_ruptura', 'Ruptura')}
            {renderRatingInput('nota_juego_de_espaldas', 'Juego de Espaldas')}
          </div>
        </div>

        {/* Notas Generales y Ajustadas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <Award className="w-5 h-5 text-blue-600" />
            Valoración General
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderRatingInput('nota_general', 'Nota General')}
            {renderRatingInput('nota_ofensiva', 'Nota Ofensiva')}
            {renderRatingInput('nota_defensiva', 'Nota Defensiva')}
            {renderRatingInput('nota_ajustada_ofensiva', 'Nota Ajustada Ofensiva')}
            {renderRatingInput('nota_ajustada_defensiva', 'Nota Ajustada Defensiva')}
          </div>
        </div>

        {/* Comentarios */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-6">
            <MapPin className="w-5 h-5 text-blue-600" />
            Observaciones
          </h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Perfil y Estilo de Juego
              </label>
              <textarea
                value={formData.perfil_estilo_juego || ''}
                onChange={(e) => handleInputChange('perfil_estilo_juego', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Describe el perfil y estilo de juego del jugador..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comentarios Generales
              </label>
              <textarea
                value={formData.comentarios || ''}
                onChange={(e) => handleInputChange('comentarios', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Comentarios adicionales sobre el rendimiento..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas Mentales
              </label>
              <textarea
                value={formData.notas_mentales || ''}
                onChange={(e) => handleInputChange('notas_mentales', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Notas sobre aspectos mentales y psicológicos..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/sporting/reportes-jugadores')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={loading || !selectedPlayer}
          >
            {loading ? (
              <>
                <LoadingSpinner />
                Creando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Crear Reporte
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReporteJugadorFormPage;
